import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Zap, Flame, Droplets, ChevronLeft, ChevronRight, ChevronRight as ChevronRightIcon, Download, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAAND_NAMEN = ['', 'Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmt = (val) => val != null ? Number(val).toLocaleString('nl-NL') : '—';

const STATUS_CONFIG = {
  compleet: { icon: '\uD83D\uDFE2', label: 'Compleet' },
  gedeeltelijk: { icon: '\uD83D\uDFE1', label: 'Gedeeltelijk' },
  leeg: { icon: '\uD83D\uDD34', label: 'Niet ingevuld' },
};

// ── Locatie Overview Table ──────────────────────────────────────────────────
const LocatieOverzicht = ({ onLocatieClick }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/management/verbruik/overzicht`, { headers: hdrs() });
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const showBronwater = data.some(l => 'bronwater_laatste' in l);

  const exportCSV = () => {
    const header = `Locatie,Stroom laatste,Datum,Gas laatste,Datum,Leidingwater laatste,Datum${showBronwater ? ',Bronwater laatste,Datum' : ''},Status`;
    const rows = data.map(l => {
      const s = l.stroom_laatste != null ? l.stroom_laatste : '';
      const g = l.gas_laatste != null ? l.gas_laatste : '';
      const w = l.water_laatste != null ? l.water_laatste : '';
      const b = ('bronwater_laatste' in l && l.bronwater_laatste != null) ? l.bronwater_laatste : '';
      const bcols = showBronwater ? `,${b},${l.bronwater_datum || ''}` : '';
      const st = STATUS_CONFIG[l.status]?.label || '';
      return `${l.locatieNaam},${s},${l.stroom_datum || ''},${g},${l.gas_datum || ''},${w},${l.water_datum || ''}${bcols},${st}`;
    });
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `verbruik-overzicht.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-5" data-testid="verbruik-overzicht-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548]" data-testid="verbruik-overzicht-title">Verbruik Overzicht</h1>
          <p className="text-slate-500 text-sm">Alle locaties — meest recente meterstanden</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" />Exporteer CSV</Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="overzicht-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-2.5 px-3 font-semibold text-slate-600">Locatie</th>
                <th className="py-2.5 px-3 font-semibold text-amber-700 whitespace-nowrap"><Zap className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Stroom</th>
                <th className="py-2.5 px-3 font-semibold text-slate-500 text-xs">Datum</th>
                <th className="py-2.5 px-3 font-semibold text-orange-700 whitespace-nowrap"><Flame className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Gas</th>
                <th className="py-2.5 px-3 font-semibold text-slate-500 text-xs">Datum</th>
                <th className="py-2.5 px-3 font-semibold text-blue-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Leidingwater</th>
                <th className="py-2.5 px-3 font-semibold text-slate-500 text-xs">Datum</th>
                {showBronwater && (
                  <>
                    <th className="py-2.5 px-3 font-semibold text-cyan-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Bronwater</th>
                    <th className="py-2.5 px-3 font-semibold text-slate-500 text-xs">Datum</th>
                  </>
                )}
                <th className="py-2.5 px-3 font-semibold text-slate-600 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data.map(l => {
                const cfg = STATUS_CONFIG[l.status] || STATUS_CONFIG.leeg;
                return (
                  <tr key={l.locatieId} onClick={() => onLocatieClick(l)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors"
                    data-testid={`overzicht-row-${l.locatieSlug}`}>
                    <td className="py-2.5 px-3 font-medium text-slate-700">{l.locatieNaam}</td>
                    <td className="py-2.5 px-3 text-slate-600 tabular-nums">{l.stroom_laatste != null ? `${fmt(l.stroom_laatste)} kWh` : '—'}</td>
                    <td className="py-2.5 px-3 text-slate-400 text-xs">{l.stroom_datum || ''}</td>
                    <td className="py-2.5 px-3 text-slate-600 tabular-nums">{l.gas_laatste != null ? <>{fmt(l.gas_laatste)} m&sup3;</> : '—'}</td>
                    <td className="py-2.5 px-3 text-slate-400 text-xs">{l.gas_datum || ''}</td>
                    <td className="py-2.5 px-3 text-slate-600 tabular-nums">{l.water_laatste != null ? <>{fmt(l.water_laatste)} m&sup3;</> : '—'}</td>
                    <td className="py-2.5 px-3 text-slate-400 text-xs">{l.water_datum || ''}</td>
                    {showBronwater && (
                      <>
                        <td className="py-2.5 px-3 text-slate-600 tabular-nums">{('bronwater_laatste' in l && l.bronwater_laatste != null) ? <>{fmt(l.bronwater_laatste)} m&sup3;</> : '—'}</td>
                        <td className="py-2.5 px-3 text-slate-400 text-xs">{l.bronwater_datum || ''}</td>
                      </>
                    )}
                    <td className="py-2.5 px-3 text-center" title={cfg.label}><span className="text-base">{cfg.icon}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-slate-500">
        <span>{STATUS_CONFIG.compleet.icon} Alle meters ingevuld deze maand</span>
        <span>{STATUS_CONFIG.gedeeltelijk.icon} Gedeeltelijk</span>
        <span>{STATUS_CONFIG.leeg.icon} Niet ingevuld</span>
      </div>
    </div>
  );
};

// ── Locatie Detail — Year Table + optional Form ─────────────────────────────
const LocatieDetail = ({ locatie, onBack }) => {
  // 4.8e: de correctie-POST staat open voor iedereen met locatietoegang; op deze
  // managementpagina corrigeren manager+ — de tekst moet dat ook zeggen.
  const { canManage } = useAuth();
  const admin = canManage?.() ?? false;
  const bronwaterAanwezig = 'bronwater_laatste' in (locatie || {});
  const now = new Date();
  const [jaar, setJaar] = useState(now.getFullYear());
  const [registraties, setRegistraties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMaand, setSelectedMaand] = useState(null);
  const formRef = React.useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/locaties/${locatie.locatieId}/verbruik?jaar=${jaar}`, { headers: hdrs() });
      if (res.ok) setRegistraties(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [locatie.locatieId, jaar]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const huidigeMaand = now.getFullYear() === jaar ? now.getMonth() + 1 : null;
  const regMap = {};
  registraties.forEach(r => { regMap[r.maand] = r; });

  const BronIcon = ({ bron }) => (
    <span className="ml-1 text-[10px] opacity-60" title={bron === 'meterstand_checklist' ? 'Via meterstandchecklist' : 'Handmatig'}>
      {bron === 'meterstand_checklist' ? '\u{1F4CB}' : '\u{270F}\u{FE0F}'}
    </span>
  );

  const handleRowClick = (m) => {
    if (admin) {
      setSelectedMaand(m);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  if (loading) return <div className="flex justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;

  return (
    <div className="space-y-6" data-testid="verbruik-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="detail-back-btn"><ArrowLeft className="w-4 h-4 mr-1" />Terug</Button>
        <div>
          <h1 className="text-xl font-bold text-[#1f2548]" data-testid="detail-title">{locatie.locatieNaam}</h1>
          <p className="text-slate-500 text-sm">Verbruik jaaroverzicht</p>
        </div>
        <div className="ml-auto flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1 py-1" data-testid="detail-jaar-selector">
          <button onClick={() => setJaar(j => j - 1)} className="p-1.5 hover:bg-slate-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
          <span className="px-3 font-semibold text-[#1f2548] text-sm tabular-nums">{jaar}</span>
          <button onClick={() => setJaar(j => j + 1)} className="p-1.5 hover:bg-slate-100 rounded" disabled={jaar >= now.getFullYear()}><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Year table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="detail-jaaroverzicht">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-2.5 px-3 font-semibold text-slate-600">Maand</th>
                <th className="py-2.5 px-3 font-semibold text-amber-700 whitespace-nowrap"><Zap className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Stand</th>
                <th className="py-2.5 px-3 font-semibold text-amber-700">Verbruik</th>
                <th className="py-2.5 px-3 font-semibold text-orange-700 whitespace-nowrap"><Flame className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Stand</th>
                <th className="py-2.5 px-3 font-semibold text-orange-700">Verbruik</th>
                <th className="py-2.5 px-3 font-semibold text-blue-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Leiding</th>
                <th className="py-2.5 px-3 font-semibold text-blue-700">Verbruik</th>
                {bronwaterAanwezig && (
                  <>
                    <th className="py-2.5 px-3 font-semibold text-cyan-700 whitespace-nowrap"><Droplets className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />Bron</th>
                    <th className="py-2.5 px-3 font-semibold text-cyan-700">Verbruik</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => {
                const r = regMap[m];
                const isCurrent = m === huidigeMaand;
                const hasData = !!r;
                const bron = r?.bron || 'handmatig';
                return (
                  <tr key={m} onClick={() => handleRowClick(m)}
                    className={`border-b border-slate-50 transition-colors ${admin ? 'cursor-pointer hover:bg-slate-50' : ''} ${isCurrent ? 'bg-[#ffc315]/5' : hasData ? '' : 'text-slate-300'}`}
                    style={isCurrent ? { borderLeft: '3px solid #ffc315' } : { borderLeft: '3px solid transparent' }}
                    data-testid={`detail-rij-${m}`}>
                    <td className={`py-2 px-3 font-medium ${isCurrent ? 'text-[#1f2548]' : hasData ? 'text-slate-700' : 'text-slate-400'}`}>
                      {MAAND_NAMEN[m]}{isCurrent && <span className="ml-1.5 text-[10px] text-[#ffc315] font-bold">NU</span>}
                    </td>
                    <td className="py-2 px-3">{hasData ? <>{fmt(r.stroom?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                    <td className="py-2 px-3 font-medium">{hasData ? `${fmt(r.stroom?.verbruik)} kWh` : '—'}</td>
                    <td className="py-2 px-3">{hasData ? <>{fmt(r.gas?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                    <td className="py-2 px-3 font-medium">{hasData ? <>{fmt(r.gas?.verbruik)} m³</> : '—'}</td>
                    <td className="py-2 px-3">{hasData ? <>{fmt(r.water?.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                    <td className="py-2 px-3 font-medium">{hasData ? <>{fmt(r.water?.verbruik)} m³</> : '—'}</td>
                    {bronwaterAanwezig && (
                      <>
                        <td className="py-2 px-3">{hasData && r.bronwater?.meterstand != null ? <>{fmt(r.bronwater.meterstand)}<BronIcon bron={bron} /></> : '—'}</td>
                        <td className="py-2 px-3 font-medium">{hasData && r.bronwater?.verbruik != null ? <>{fmt(r.bronwater.verbruik)} m³</> : '—'}</td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin correction form */}
      {admin && selectedMaand && (
        <div ref={formRef} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="detail-form-section">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
            <h2 className="font-semibold text-[#1f2548]">Correctie — {MAAND_NAMEN[selectedMaand]} {jaar}</h2>
          </div>
          <div className="p-5">
            <AdminCorrectionForm locatieId={locatie.locatieId} jaar={jaar} maand={selectedMaand} onSaved={fetchData} bronwaterAanwezig={bronwaterAanwezig} />
          </div>
        </div>
      )}

      {!admin && (
        <p className="text-xs text-slate-400 text-center" data-testid="correctie-uitleg">
          Corrigeren gaat via de locatiepagina Verbruik; hier corrigeren managers en admins.
        </p>
      )}
    </div>
  );
};

// ── Admin Correction Form (reuse logic) ─────────────────────────────────────
const AdminCorrectionForm = ({ locatieId, jaar, maand, onSaved, bronwaterAanwezig }) => {
  const [form, setForm] = useState({ stroomMeterstand: '', gasMeterstand: '', waterMeterstand: '', bronwaterMeterstand: '', notities: '' });
  const [vorige, setVorige] = useState({ stroom: null, gas: null, water: null, bronwater: null });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!locatieId || !jaar || !maand) return;
    setLoading(true);
    Promise.all([
      fetch(`${API}/locaties/${locatieId}/verbruik/${jaar}/${maand}`, { headers: hdrs() }),
      fetch(`${API}/locaties/${locatieId}/verbruik/vorige-meterstand?jaar=${jaar}&maand=${maand}`, { headers: hdrs() })
    ]).then(async ([regRes, prevRes]) => {
      if (regRes.ok) {
        const d = await regRes.json();
        setForm({ stroomMeterstand: d.stroom?.meterstand ?? '', gasMeterstand: d.gas?.meterstand ?? '', waterMeterstand: d.water?.meterstand ?? '', bronwaterMeterstand: d.bronwater?.meterstand ?? '', notities: d.notities || '' });
      } else {
        setForm({ stroomMeterstand: '', gasMeterstand: '', waterMeterstand: '', bronwaterMeterstand: '', notities: '' });
      }
      if (prevRes.ok) setVorige(await prevRes.json());
    }).catch(() => {}).finally(() => setLoading(false));
  }, [locatieId, jaar, maand]);

  const calc = (ms, key) => {
    if (!ms || ms === '') return null;
    const c = parseInt(ms, 10);
    if (isNaN(c)) return null;
    return Math.max(0, c - (vorige[key]?.meterstand ?? 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.stroomMeterstand || !form.gasMeterstand || !form.waterMeterstand) { toast.error('Vul alle meterstanden in'); return; }
    if (bronwaterAanwezig && !form.bronwaterMeterstand) { toast.error('Vul ook de bronwater-meterstand in'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/locaties/${locatieId}/verbruik`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ jaar, maand, stroom: { meterstand: Number(form.stroomMeterstand) }, gas: { meterstand: Number(form.gasMeterstand) }, water: { meterstand: Number(form.waterMeterstand) }, notities: form.notities, ...(bronwaterAanwezig ? { bronwater: { meterstand: Number(form.bronwaterMeterstand) } } : {}) })
      });
      if (res.ok) { toast.success('Correctie opgeslagen'); onSaved?.(); }
      else { const err = await res.json(); toast.error(err.detail || 'Opslaan mislukt'); }
    } catch { toast.error('Opslaan mislukt'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="admin-correction-form">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs flex items-center gap-1"><Zap className="w-3 h-3 text-amber-600" />Stroom meterstand (kWh)</Label>
          <Input type="number" value={form.stroomMeterstand} onChange={e => setForm(p => ({ ...p, stroomMeterstand: e.target.value }))} className="h-9" data-testid="admin-stroom-input" />
          {calc(form.stroomMeterstand, 'stroom') !== null && <p className="text-[10px] text-slate-400 mt-0.5">Verbruik: {Number(calc(form.stroomMeterstand, 'stroom')).toLocaleString('nl-NL')} kWh</p>}
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Flame className="w-3 h-3 text-orange-600" />Gas meterstand (m³)</Label>
          <Input type="number" value={form.gasMeterstand} onChange={e => setForm(p => ({ ...p, gasMeterstand: e.target.value }))} className="h-9" data-testid="admin-gas-input" />
          {calc(form.gasMeterstand, 'gas') !== null && <p className="text-[10px] text-slate-400 mt-0.5">Verbruik: {Number(calc(form.gasMeterstand, 'gas')).toLocaleString('nl-NL')} m³</p>}
        </div>
        <div>
          <Label className="text-xs flex items-center gap-1"><Droplets className="w-3 h-3 text-blue-600" />Leidingwater meterstand (m³)</Label>
          <Input type="number" value={form.waterMeterstand} onChange={e => setForm(p => ({ ...p, waterMeterstand: e.target.value }))} className="h-9" data-testid="admin-water-input" />
          {calc(form.waterMeterstand, 'water') !== null && <p className="text-[10px] text-slate-400 mt-0.5">Verbruik: {Number(calc(form.waterMeterstand, 'water')).toLocaleString('nl-NL')} m³</p>}
        </div>
        {bronwaterAanwezig && (
          <div>
            <Label className="text-xs flex items-center gap-1"><Droplets className="w-3 h-3 text-cyan-600" />Bronwater meterstand (m³)</Label>
            <Input type="number" value={form.bronwaterMeterstand} onChange={e => setForm(p => ({ ...p, bronwaterMeterstand: e.target.value }))} className="h-9" data-testid="admin-bronwater-input" />
            {calc(form.bronwaterMeterstand, 'bronwater') !== null && <p className="text-[10px] text-slate-400 mt-0.5">Verbruik: {Number(calc(form.bronwaterMeterstand, 'bronwater')).toLocaleString('nl-NL')} m³</p>}
          </div>
        )}
      </div>
      <Textarea placeholder="Notities..." value={form.notities} onChange={e => setForm(p => ({ ...p, notities: e.target.value }))} rows={2} className="text-sm" />
      <Button type="submit" disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="admin-correction-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Correctie opslaan
      </Button>
    </form>
  );
};

// ── Main Page (Overview + Detail toggle) ────────────────────────────────────
const VerbruikOverzicht = () => {
  const [selectedLocatie, setSelectedLocatie] = useState(null);

  if (selectedLocatie) {
    return <LocatieDetail locatie={selectedLocatie} onBack={() => setSelectedLocatie(null)} />;
  }
  return <LocatieOverzicht onLocatieClick={setSelectedLocatie} />;
};

export default VerbruikOverzicht;
